import "./style.scss";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import {
    getVariableGroupI18nKey,
    groupTemplateVariableEntries,
} from "components/ContractsSettings/ContractEditorLexical/groupTemplateVariables";
import { VARIABLES_WITH_SECONDARY_CHOICE } from "components/ContractsSettings/ContractEditorLexical/variables";
import FilterInput from "components/shared/FilterInput";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { SmallBody } from "helpers/TypographyComponents";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import { EPortalType } from "store/types";

interface InputProps {
    variables: Record<string, string>;
    onSelect: (value: string) => void;
}

const InsertVariable = ({ variables, onSelect }: InputProps) => {
    const [t] = useTranslation();
    const { portalType } = useCompanySettings();
    const { customFields } = useAppSelector((state) => state.customFields);
    const resolvedPortalType = portalType ?? EPortalType.BOOK;
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchVariableQuery, setSearchVariableQuery] = useState("");
    const open = Boolean(anchorEl);

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        // timeout, clear search query after closing animation
        setTimeout(() => {
            setSearchVariableQuery("");
        }, 500);
    };

    const handleSelectVariable = (value: string) => {
        setAnchorEl(null);
        onSelect(value);
    };

    const filteredVariableGroups = useMemo(() => {
        const entries = Object.entries(variables);
        const matchingEntries = searchVariableQuery
            ? entries.filter(([, label]) => label.toLowerCase().includes(searchVariableQuery.toLowerCase()))
            : entries;
        return groupTemplateVariableEntries(matchingEntries, resolvedPortalType, customFields);
    }, [customFields, resolvedPortalType, searchVariableQuery, variables]);

    return (
        <>
            <button className="insert-variable-btn" onClick={handleClick}>
                <div className="btn-text">{t("ContractSettings.Variables")}</div>
                <div className="btn-arrow">
                    <ExpandMoreIcon sx={{ height: "18px", width: "18px" }} />
                </div>
            </button>
            <Menu
                id="menu"
                open={open}
                onClose={handleClose}
                className="insert-variable-popover"
                elevation={0}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <FilterInput
                    filterValue={searchVariableQuery}
                    setFilterValue={setSearchVariableQuery}
                    size="small"
                    style={{ padding: "0.25rem" }}
                />
                {filteredVariableGroups.length > 0 ? (
                    <ScrollShadowWrapper style={{ maxHeight: "26rem" }}>
                        {filteredVariableGroups.map((group) => (
                            <div className="insert-variable-group" key={group.id}>
                                <div className="insert-variable-group-label">
                                    {t(getVariableGroupI18nKey(group.id, resolvedPortalType))}
                                </div>
                                {group.items.map(([key, value]) => {
                                    const hasSecondaryChoice = VARIABLES_WITH_SECONDARY_CHOICE.has(key);
                                    return (
                                        <MenuItem
                                            key={key}
                                            onClick={() => handleSelectVariable(key)}
                                            sx={{
                                                fontSize: "13px",
                                                fontFamily: "Poppins",
                                                borderBottom: "1px solid rgb(234, 234, 234)",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                gap: "0.5rem",
                                            }}
                                        >
                                            <span>{value}</span>
                                            {hasSecondaryChoice && (
                                                <Tooltip
                                                    title={t("ContractSettings.VariableRequiresSecondaryChoice")}
                                                    placement="right"
                                                >
                                                    <ChevronRightIcon
                                                        sx={{
                                                            height: "16px",
                                                            width: "16px",
                                                            color: "rgba(0, 0, 0, 0.45)",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </MenuItem>
                                    );
                                })}
                            </div>
                        ))}
                    </ScrollShadowWrapper>
                ) : (
                    <SmallBody color="grey" style={{ padding: "0.25rem", paddingLeft: "0.5rem" }}>
                        {t("RichEditor.NoResults")}
                    </SmallBody>
                )}
            </Menu>
        </>
    );
};

export default InsertVariable;
